import { DateTime, Duration, Interval, Settings } from 'luxon';
import './time-picker.scss';
import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { parseIsoTime } from '../../../util/date-time-util';
import { Typography } from '../../typography/typography';

export class TimePickerDefaults {
  static readonly step = Duration.fromObject({ minutes: 30 });
  static readonly min = '00:00';
  static readonly max = '23:59';
  static readonly restrictToTimes = [];
}

export const TimePicker = ({
  value,
  onChange,
  disabledIntervals = [],
  highlightedIntervals = [],
  step = TimePickerDefaults.step,
  min = TimePickerDefaults.min,
  max = TimePickerDefaults.max,
  restrictToTimes = TimePickerDefaults.restrictToTimes,
  timezone = Settings.defaultLocale,
}: {
  value?: DateTime;
  onChange?: (d: DateTime) => void;
  disabledIntervals?: Interval[];
  highlightedIntervals?: { intervals: Interval; color: string }[];
  step?: Duration;
  min?: string;
  max?: string;
  restrictToTimes?: string[];
  timezone?: string;
}) => {
  const startTime = (value?.isValid ? value : DateTime.local({ zone: timezone })).set(parseIsoTime(min));
  const endTime = startTime.set(parseIsoTime(max));
  const selectedRef = useRef<HTMLLIElement>(null);
  const times = [];
  let curTime = startTime;
  while (curTime.diff(endTime).milliseconds <= 0) {
    if (!restrictToTimes.length || restrictToTimes.find((t) => t === curTime.toLocaleString(DateTime.TIME_24_SIMPLE))) {
      times.push(curTime);
    }
    curTime = curTime.plus(step);
  }

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [value]);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ behavior: 'auto', block: 'center' });
  }, []);

  return (
    <div className="time-picker">
      <ol>
        {times.map((t) => {
          const diffMinutes = value && t?.diff(value, 'minutes').minutes;
          const selected = diffMinutes !== undefined && diffMinutes >= 0 && diffMinutes < step.minutes;
          const disabled = t && disabledIntervals.find((i) => i.contains(t));
          const highlighted = t && highlightedIntervals?.find((i) => i.intervals.contains(t));
          return (
            <li
              {...{ ref: selected ? selectedRef : null }}
              key={t.toLocaleString(DateTime.TIME_SIMPLE)}
              className={classNames({ selected, disabled })}
              style={highlighted && { backgroundColor: highlighted.color }}
              onClick={() => onChange && onChange(t)}
            >
              <Typography type="text-sm"> {t.toLocaleString(DateTime.TIME_SIMPLE)}</Typography>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
